import { Skeleton } from 'antd'
import React, { useState } from 'react'
import { Link } from 'react-router'
import { Text } from 'sutra/components/Text/Text'

import {
  EmptyState,
  ExpandChevron,
  ExpandLink,
  LinkText,
  PanelCard,
  PanelHeader,
  PanelTitle,
  Row,
  RowList,
  RowSubtitle,
  RowTextStack,
  RowTimestamp,
  SkeletonWrap,
  SubtitleEditButton,
  TimelineCell,
  TimelineDot,
} from 'features/trend-detection/features/issue-agent-detail/components/ActivityFeed/styles'
import { summarizeRun } from 'features/trend-detection/features/issue-agent-detail/utils/format-run'
import type { RunSummarizable } from 'features/trend-detection/features/issue-agent-detail/utils/format-run'
import { formatRelativeTimestamp } from 'utils/relative-time'

const TRUNCATE_LIMIT = 10

export type InsightsRecency = 'latest' | 'earlier'

export type ActivityFeedRun = RunSummarizable & { uuid: string }

export interface ActivityFeedProps {
  runs: ActivityFeedRun[]
  isLoading: boolean
  latestInsightsCount: number
  getInsightsHref: (recency: InsightsRecency) => string
  /** When provided, config-class failure rows render an inline "Edit filters"
   *  link. Omitted by viewer surfaces — the row shows subtitle text alone. */
  onEditFilters?: () => void
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  runs,
  isLoading,
  latestInsightsCount,
  getInsightsHref,
  onEditFilters,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const canTruncate = runs.length > TRUNCATE_LIMIT
  const visibleRuns =
    canTruncate && !isExpanded ? runs.slice(0, TRUNCATE_LIMIT) : runs
  const topRowRecency: InsightsRecency =
    latestInsightsCount > 0 ? 'latest' : 'earlier'
  return (
    <PanelCard>
      <PanelHeader>
        <PanelTitle forwardedAs="h2">Agent activity</PanelTitle>
      </PanelHeader>
      {isLoading ? (
        <SkeletonWrap>
          <Skeleton active paragraph={{ rows: 5 }} title={false} />
        </SkeletonWrap>
      ) : runs.length === 0 ? (
        <EmptyState>
          No runs yet — the first scheduled run will appear here.
        </EmptyState>
      ) : (
        <RowList>
          {visibleRuns.map((run, index) => {
            const summary = summarizeRun(run)
            const isTop = index === 0
            const isFoundLink = isTop && summary.variant === 'found'
            const isEmptyLink = isTop && summary.variant === 'empty'
            const isNavigable = isFoundLink || isEmptyLink
            const href = getInsightsHref(topRowRecency)
            return (
              <Row
                key={run.uuid}
                data-variant={summary.variant}
                $clickable={isNavigable}
              >
                <TimelineCell aria-hidden>
                  <TimelineDot $variant={summary.variant} />
                </TimelineCell>
                {isFoundLink ? (
                  <LinkText forwardedAs={Link} to={href} $stretch>
                    {summary.text}
                  </LinkText>
                ) : isEmptyLink ? (
                  <Text $size="sm" as="span">
                    {summary.text}{' '}
                    <LinkText forwardedAs={Link} to={href} $stretch>
                      (see earlier)
                    </LinkText>
                  </Text>
                ) : summary.subtitle ? (
                  <RowTextStack>
                    <Text $size="sm" as="span">
                      {summary.text}
                    </Text>
                    <RowSubtitle forwardedAs="span">
                      {summary.subtitle}
                      {summary.showEditFiltersLink && onEditFilters && (
                        <>
                          {' '}
                          <SubtitleEditButton
                            type="button"
                            onClick={onEditFilters}
                          >
                            Edit filters
                          </SubtitleEditButton>
                        </>
                      )}
                    </RowSubtitle>
                  </RowTextStack>
                ) : (
                  <Text $size="sm" as="span">
                    {summary.text}
                  </Text>
                )}
                <RowTimestamp forwardedAs="span">
                  {formatRelativeTimestamp(summary.timestampIso)}
                </RowTimestamp>
              </Row>
            )
          })}
        </RowList>
      )}
      {!isLoading && canTruncate && (
        <ExpandLink
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Show less' : 'View all activity'}
          <ExpandChevron $isExpanded={isExpanded} width={16} height={16} />
        </ExpandLink>
      )}
    </PanelCard>
  )
}
